import React, {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { AppStore, userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch } from '~/initializeStore';
import { deadlineGuardConfig } from '~/config/deadlineGuardConfig';
import { workspaceStore } from '~/config/workspaceStore';
import { fetchWorkspaces } from '~/api/workspaces';
import type { Workspace } from '~/types/workspace';
import { safeContinuePath } from '~/utils/safeContinuePath';

interface OwnProps {}

type Props = PropsWithChildren<OwnProps>;

const AuthChecker: FunctionComponent<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { i18n, t } = useTranslation(['dashboard']);
  const [isUserResolved, setIsUserResolved] = useState(false);
  const user = useSelector((store: AppStore) => store.user);
  const userUid = user.data?.uid;
  const query = useQuery();
  const syncedUsers = useRef<Set<string>>(new Set());
  const bootstrappedUsers = useRef<Set<string>>(new Set());
  const activeUid = useRef<string | null>(null);
  const sessionId = useRef(0);

  const handleUserData = useCallback(
    async (newUser: firebase.User | null) => {
      setIsUserResolved(true);

      if (!newUser) {
        activeUid.current = null;
        sessionId.current += 1;
        syncedUsers.current.clear();
        bootstrappedUsers.current.clear();
        workspaceStore.reset();

        return;
      }
      // Licznik sesji rośnie przy każdej zmianie użytkownika (także wylogowaniu), więc spóźniony
      // wynik poprzedniego przebiegu nie nadpisze stanu nowej sesji — nawet dla tego samego konta.
      if (activeUid.current !== newUser.uid) {
        sessionId.current += 1;
        if (activeUid.current !== null) {
          // Zmiana konta bez wylogowania (impersonacja): stan poprzedniego użytkownika nie może
          // przetrwać — powrót do tego samego konta ma zrobić świeży sync + bootstrap.
          syncedUsers.current.clear();
          bootstrappedUsers.current.clear();
          workspaceStore.reset();
        }
      }
      activeUid.current = newUser.uid;
      const mySession = sessionId.current;
      const isStale = () => sessionId.current !== mySession;

      if (newUser.uid !== userUid) {
        dispatch(userActions.setLoggedUserData(newUser));
      }

      const uid = newUser.uid;

      // syncUser nie rzuca — porażkę (GET/POST users: 5xx, timeout, 401…) zgłasza zwracając null.
      const runSync = async (): Promise<boolean> => {
        if (syncedUsers.current.has(uid)) {
          return true;
        }
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const language = i18n.language || 'en';
          const result = await deadlineGuardConfig.syncUser(timezone, language);
          if (result === null || result === undefined) {
            return false;
          }
          if (!isStale()) {
            syncedUsers.current.add(uid);
          }

          return true;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to sync user with DeadlineGuard service:', error);

          return false;
        }
      };

      // Workspace'y dopiero po synchronizacji: POST /api/users tworzy domyślny workspace, więc
      // równoległy GET /api/workspaces zwracał pustą listę nowemu użytkownikowi (T-327).
      // Porażka sync nie blokuje istniejącego użytkownika — lista i tak jest pobierana; pusta
      // lista po nieudanym sync to błąd bootstrapu (konto mogło nie dostać domyślnego workspace'u).
      const syncThenBootstrap = async () => {
        if (bootstrappedUsers.current.has(uid)) {
          return;
        }
        bootstrappedUsers.current.add(uid);
        const synced = await runSync();
        if (isStale()) {
          return;
        }
        let list: Workspace[] | null = null;
        let fetchError: string | null = null;
        try {
          list = await fetchWorkspaces();
        } catch (error) {
          fetchError = error instanceof Error ? error.message : 'Failed to load workspaces';
        }
        // Zapis do store'u dopiero po sprawdzeniu sesji — spóźniona odpowiedź nic nie zmienia.
        if (isStale()) {
          return;
        }
        if (list === null) {
          bootstrappedUsers.current.delete(uid);
          workspaceStore.loadWorkspaces([]);
          workspaceStore.setBootstrapError(fetchError);
        } else {
          workspaceStore.loadWorkspaces(list);
          if (!synced && list.length === 0) {
            bootstrappedUsers.current.delete(uid);
            workspaceStore.setBootstrapError(t('dashboard:workspaces.syncError'));
          }
        }
        workspaceStore.setBootstrapReady(true);
      };

      workspaceStore.setRetryHandler(() => {
        workspaceStore.setBootstrapError(null);
        workspaceStore.setBootstrapReady(false);
        void syncThenBootstrap();
      });

      void syncThenBootstrap();

      if (history.location.pathname.startsWith('/auth')) {
        history.push(safeContinuePath(query.get('continue')));
      }
    },
    [dispatch, history, i18n.language, query, t, userUid]
  );

  const debouncedCallback = useDebouncedCallback(handleUserData, 400);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(debouncedCallback);

    return () => unsubscribe();
  }, [debouncedCallback]);

  if (!isUserResolved) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthChecker;
